import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Globe, Smartphone, Puzzle, Languages, Store, Code } from 'lucide-react';

const CloudSection = () => {
  const cloudFeatures = [
    {
      icon: <Globe className="h-8 w-8 text-blue-600" />,
      title: "Von überall arbeiten",
      description: "Egal ob im Büro, zu Hause oder beim Kunden - öffne einfach deinen Browser und schon hast du Zugriff auf alle deine Daten. Kein VPN, keine komplizierten Einstellungen."
    },
    {
      icon: <Smartphone className="h-8 w-8 text-green-600" />,
      title: "Auch unterwegs dabei",
      description: "Mit der App für iPhone und Android hast du die wichtigsten Funktionen immer in der Hosentasche. Schnell mal einen Kunden anlegen oder Lagerbestände prüfen - kein Problem."
    },
    {
      icon: <Puzzle className="h-8 w-8 text-purple-600" />,
      title: "Funktioniert mit deinen gewohnten Programmen",
      description: "Du arbeitest schon mit Outlook und Excel? Super! Business Central fügt sich nahtlos ein. Kundeninformationen direkt in Outlook, Berichte in Excel - alles wie gewohnt."
    },
    {
      icon: <Languages className="h-8 w-8 text-orange-600" />,
      title: "Für internationale Geschäfte",
      description: "Verkaufst du ins Ausland? Business Central kann mit verschiedenen Währungen, Sprachen und Steuerregeln umgehen. Einfach das entsprechende Länderpaket aktivieren."
    },
    {
      icon: <Store className="h-8 w-8 text-teal-600" />,
      title: "Viele Erweiterungen verfügbar",
      description: "Im Microsoft AppSource Store findest du hunderte von fertigen Erweiterungen für spezielle Anforderungen. Von Branchenlösungen bis zu speziellen Funktionen."
    },
    {
      icon: <Code className="h-8 w-8 text-indigo-600" />,
      title: "Individuelle Anpassungen möglich",
      description: "Business Central lässt sich durch die AL-Programmiersprache sehr gut an deine Abläufe anpassen. Dokumente, Felder und Prozesse können erweitert werden - dafür brauchst du aber einen Entwickler."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Wie funktioniert Business Central?
          </h2>
          <p className="text-lg text-gray-600">
            Vergiss komplizierte Server und teure IT-Projekte. Business Central läuft in der Microsoft-Cloud - 
            du nutzt es einfach wie Netflix oder Spotify.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {cloudFeatures.map((feature, index) => (
            <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-xl p-8 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">
              Das Beste: Du musst dich um nichts kümmern
            </h3>
            <p className="text-lg text-gray-600">
              Microsoft kümmert sich um Server, Sicherheit und Updates. Du konzentrierst dich auf dein Geschäft.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 mb-2">Immer aktuell</div>
              <p className="text-sm text-blue-800">Neue Funktionen kommen automatisch - ohne dass du etwas tun musst</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600 mb-2">Sicher in Deutschland</div>
              <p className="text-sm text-green-800">Deine Daten bleiben in deutschen Rechenzentren</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 mb-2">Einfach zu bedienen</div>
              <p className="text-sm text-purple-800">Outlook und Excel werden zu mächtigen Geschäftstools</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-6">
              Business Central ist der moderne Nachfolger von Dynamics NAV - nur viel einfacher und flexibler.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Alle Funktionen ansehen
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CloudSection; 